import { inject } from "@angular/core";
import { Observable, catchError, map, of } from "rxjs";
import { CookieService } from "ngx-cookie-service";
import {
    CanActivateFn,
    ActivatedRouteSnapshot,
    RouterStateSnapshot,
    Router,
    UrlTree,
    CanActivateChildFn
} from "@angular/router";

import { UserService } from "../services/user.service";
import { LoggerService } from "../../shared/services/logger.service";
import { UserProfileResponse } from "../../shared/models/request/user-profile.response";
import { CONSTANTS, COOKIE, SPLUNK } from "../../modules/login/util/constants";
import { Constants } from "../../shared/constants/constants";
import { APP_CONFIG } from "../services/app-config.service";

export const authGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
): Observable<boolean | UrlTree> | boolean | UrlTree => {

    const userService = inject(UserService);
    const cookieService = inject(CookieService);
    const loggerService = inject(LoggerService);
    const router = inject(Router);

    const isUserNameAvailable: boolean = cookieService.check(COOKIE.DP_ADM_USER_NAME);
    const isSessionIdAvailable: boolean = cookieService.check(COOKIE.DP_ADM_SID);
    const isJwtTokenAvailable: boolean = cookieService.check(COOKIE.DP_ADM_TOKEN);
    const isUserIdAvailable: boolean = cookieService.check(COOKIE.DP_ADM_USER_ID);
    if (!state.url.includes(CONSTANTS.HOME_URL) && state.url.includes(Constants.BENEFITS_ROUTE)) {
        cookieService.set(COOKIE.BENEFIT_LOOKUP_URL, state.url,1,'/','',true,'Strict');
    }

    if (isUserNameAvailable && isSessionIdAvailable && isJwtTokenAvailable && isUserIdAvailable) {
        if (userService.getUserName) {
            return authenticateUser(route, state, userService, loggerService, router, cookieService);
        } else {
            try {
                // Restore Session ID
                const sid: string = atob(cookieService.get(COOKIE.DP_ADM_SID));
                userService.setSessionID = sid;

                // Restore JWT Token
                const token: string = atob(cookieService.get(COOKIE.DP_ADM_TOKEN));
                userService.setJwtToken = token;

                // Restore User Id
                const userId: string = atob(cookieService.get(COOKIE.DP_ADM_USER_ID));
                userService.setUserIdentifier = userId;

                // Restore User Name
                const userName: string = atob(cookieService.get(COOKIE.DP_ADM_USER_NAME));
                userService.setUserName = userName;
                const request = { userName: userName };

                // Restore User data
                return userService.userProfileRequest(request)
                    .pipe(
                        map((data: UserProfileResponse) => {
                            if (data?.responseHeader?.transactionNotification?.status === CONSTANTS.SUCCESS) {
                                // Set user profile
                                userService.setUserProfile = data.responseBody;
                                // Call session timeout logic
                                userService.handleSessionTimeout();
                                // Set user as authenticated
                                userService.setAuthenticated = true;
                                userService.isAuntheticated$.next(true);

                                // Splunk Update
                                loggerService.logEvent(
                                    SPLUNK.USER_PROFILE_SUCCESS_EVENT_CD,
                                    SPLUNK.USER_PROFILE_SUCCESS_EVENT_DESC,
                                    SPLUNK.USER_PROFILE_SUCCESS_ID,
                                    '',
                                    {
                                        adminUserId: userService.getUserIdentifier,
                                        adminUserName: userService.getUserName
                                    },
                                    CONSTANTS.SUCCESS
                                );

                                return authenticateUser(route, state, userService, loggerService, router, cookieService);
                            } else {
                                return navigateToLogin(userService, router, loggerService, userName);
                            }
                        }), catchError(() => {
                            return of(navigateToLogin(userService, router, loggerService, userName));
                        })
                    );
            } catch (error) {
                return of(navigateToLogin(userService, router, loggerService, Constants.UNAUTHUSER));
            }
        }
    } else {
        userService.logoutUser();
        const benefitLookupUrl = cookieService.get(COOKIE.BENEFIT_LOOKUP_URL);
        if ((benefitLookupUrl !== null && benefitLookupUrl !== "") && benefitLookupUrl !== undefined) {
            window.location.href = APP_CONFIG.DPADMIN_AZURE_SSO_URL;
            return false;
        } else {
            return router.createUrlTree(['login']);
        }
    }
};

export const authGuardChild: CanActivateChildFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
) => authGuard(route, state);

const authenticateUser = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    userService: UserService,
    loggerService: LoggerService,
    router: Router,
    cookieService: CookieService
): boolean | UrlTree => {

    // If user is not authenticated, redirect to login
    if (!userService.isAuthenticated) {
        userService.logoutUser();
        return router.createUrlTree(['login']);
    }

    const userAccessList = [userService.getUserRole, ...userService.getUserPrivilegesList];
    const requiredAccessList = route.data['requiredAccess'] || [];

    // If user does not have access to the page, redirect to home
    if (requiredAccessList.length && !requiredAccessList.some((access: string) => userAccessList.includes(access))) {

        // Update splunk on unauthorized url access
        loggerService.logEvent(
            SPLUNK.UNAUTH_URL_ACCESS_ATTEMPT_CD,
            SPLUNK.UNAUTH_URL_ACCESS_ATTEMPT_DESC,
            SPLUNK.UNAUTH_URL_ACCESS_ATTEMPT_ID,
            '',
            {
                adminUserName: userService.getUserName,
                adminUserId: userService.getUserIdentifier,
                auditMessageText: `${CONSTANTS.UNAUTHORIZED_AUDIT_TXT} ${state.url}`
            },
            CONSTANTS.UNAUTHORIZED_ACCESS
        );

        return router.createUrlTree(['home']);
    }

    return true;
}

const navigateToLogin = (
    userService: UserService,
    router: Router,
    loggerService: LoggerService,
    userName: string
): boolean | UrlTree => {
    loggerService.logEvent(
        SPLUNK.USER_PROFILE_FAILURE_EVENT_CD,
        SPLUNK.USER_PROFILE_FAILURE_EVENT_DESC,
        SPLUNK.USER_PROFILE_FAILURE_ID,
        '',
        {
            adminUserName: userName
        },
        CONSTANTS.FAILURE
    );
    userService.logoutUser();
    return router.createUrlTree(['login']);
}